/**
 * The PN consultation as it is recorded on the chart — derivations shared by the
 * Orbit card, the Intake & billing panel and the bots that act on it.
 *
 * It rides on the intro-call note for now: same `ci-intro-call-note` category,
 * same `ci-intro-call-answers` payload, told apart by
 * `visitType === 'pn-consultation'`. A category of its own — so PN consultations
 * count separately from PAC intro calls — is deferred, not rejected; when it
 * lands, this module is where the predicate changes.
 *
 * Parsing, draft state and "has anything been answered" are the intro-call
 * note's (`patient-access-note.ts`); only what the PN script records
 * differently lives here.
 */

import { GOAL_SHORT_LABELS } from './patient-access-script';
import { PatientTrackLabel, Track } from './constants';
import {
  answerString,
  answerStrings,
  hasAnyRecordedValue,
  PacIntroCallAnswers,
  PN_CONSULTATION_VISIT_TYPE,
} from './patient-access-note';
import {
  describeEmployerBenefit,
  describeVerificationTask,
  PN_BOOK_ONCE_APPROVED_ID,
  PN_EMPLOYER_TASK_ID,
  PN_INSURANCE_BOOK_ONCE_APPROVED_ID,
  PN_INSURANCE_ELIGIBILITY_ID,
  PN_INSURANCE_GROUP_ID,
  PN_INSURANCE_MEMBER_ID,
  PN_INSURANCE_TASK_ID,
  PN_INSURANCE_PREFILLED_ID,
  insuranceTravelMarket,
  readVerificationTask,
  PN_INSURANCE_SOURCE_ID,
  PN_INSURANCE_TRAVEL_ID,
  PN_INSURER_ID,
  PN_POLICYHOLDER_ID,
  PN_EMPLOYER_ID,
  PN_EMPLOYMENT_ID,
  PN_BENEFITS_THROUGH_ID,
  PN_EMPLOYMENT_STATUS_ID,
  PN_FAMILY_EMPLOYER_ID,
  PN_FAMILY_MEMBER_NAME_ID,
  PN_OUTCOME_INTENT,
  PnOutcome,
} from './pn-consultation-script';
import { readWeightRelatedConditions } from './weight-related-conditions';
import {
  PN_CARD_TRACK_ID,
  PN_CARD_VERSION_ID,
  PN_EMPLOYER_LOCATION_ID,
  readPaymentTypes,
} from './pn-consultation-v2-script';

export { PN_CONSULTATION_VISIT_TYPE };

/** The PN consultation's answers are the intro-call note's, on the PN visit type. */
export type PnConsultationAnswers = PacIntroCallAnswers;

export const EMPTY_PN_CONSULTATION_ANSWERS: PnConsultationAnswers = {
  visitType: PN_CONSULTATION_VISIT_TYPE,
  values: {},
};

export function getPnConsultationOutcome(answers: PnConsultationAnswers): PnOutcome | undefined {
  return answerString(answers.values.close) as PnOutcome | undefined;
}

/**
 * The Intake hub's Qualified filter (BAR-2975): each Outcome with a stable code
 * for the Patient filter tag and a short label, most committed first. Codes, not
 * the Outcome text, so a wording change in the script keeps the tag.
 */
export const PN_QUALIFICATION_LEVELS: readonly { outcome: PnOutcome; code: string; label: string }[] = [
  { outcome: 'Committed, likely payment in 72 hours', code: 'committed-72h', label: 'Committed (72h)' },
  { outcome: 'Committed, benefit verification next', code: 'committed-benefits', label: 'Committed (benefits)' },
  { outcome: 'Likely, still deciding', code: 'likely', label: 'Likely' },
  { outcome: 'Lukewarm, will decide soon', code: 'lukewarm', label: 'Lukewarm' },
  { outcome: 'Maybe in the future', code: 'future', label: 'Future' },
  { outcome: 'Not qualified', code: 'not-qualified', label: 'Not qualified' },
  { outcome: 'Not interested', code: 'not-interested', label: 'Not interested' },
];

export function pnQualificationCode(outcome: string | undefined): string | undefined {
  return PN_QUALIFICATION_LEVELS.find((level) => level.outcome === outcome)?.code;
}

/**
 * The intent signal a saved note carries, if any — `high` fires Lychee, `medium`
 * Plum, `coverage` the PostHog-only `pn_consult_coverage_intent`.
 */
export function getPnConsultationIntent(answers: PnConsultationAnswers): 'high' | 'medium' | 'coverage' | undefined {
  const outcome = getPnConsultationOutcome(answers);
  return outcome ? PN_OUTCOME_INTENT[outcome] : undefined;
}

/**
 * What still blocks Save to chart, or undefined when the note may be saved.
 *
 * The outcome is required: it is the navigator's read of the call and the one
 * field the ad-platform signals key off. The card disables Save and shows this
 * line until it is set.
 */
export function pnConsultationSaveBlocker(answers: PnConsultationAnswers): string | undefined {
  return getPnConsultationOutcome(answers) ? undefined : 'Record an outcome to save';
}

/** The patient's track as the card last read it while the note was worked (BAR-2925). Not an answer. */
export const PN_CALL_TRACK_ID = 'call_track';

/** Status before an outcome is recorded. */
export const PN_IN_PROGRESS_STATUS = 'In progress';
export const PN_NOT_STARTED_STATUS = 'Not started';

/**
 * The track after the call: what the card recorded, which a coverage button's
 * switch moves at once and a later read keeps in step. A note with no recorded
 * track (its track was never read, or it predates this) falls back to the
 * switch a button recorded.
 */
function trackAfterCall(values: PnConsultationAnswers['values']): Track | undefined {
  const recorded = answerString(values[PN_CALL_TRACK_ID]);
  if (Object.values(Track).includes(recorded as Track)) return recorded as Track;
  return [PN_INSURANCE_TASK_ID, PN_EMPLOYER_TASK_ID]
    .map((id) => readVerificationTask(values[id]))
    .find((record) => record?.outcome === 'switched')?.track;
}

/**
 * Whether the navigator has answered anything — a chosen slot or procedure date
 * included: values the card wrote itself
 * don't count — the recorded track, the v2.0 track chip, the computed eligibility, and what it
 * prefilled from signup into the insurance section. The eligibility form's
 * prefill (procedure, location) is not tracked, so it still counts.
 */
export interface PreferredProcedureDate {
  /** The board date the patient agreed to, as an ISO date. */
  date?: string;
  /** Otherwise the coarse timing chip, e.g. "In the next couple of months". */
  timing?: string;
}

/** The To schedule tab's Preferred date (BAR-3096): the agreed date, else the timing, else nothing. */
export function readPreferredProcedureDate(answers: PnConsultationAnswers): PreferredProcedureDate | undefined {
  const date = answers.procedureDate?.start;
  if (date) return { date };
  const timing = answerString(answers.values.procdate);
  return timing ? { timing } : undefined;
}

export function hasPnConsultationAnswer(answers: PnConsultationAnswers): boolean {
  if (answers.slot || answers.procedureDate) return true;
  const { values } = answers;
  const own = new Set([
    PN_CALL_TRACK_ID,
    // The v2.0 track chip changes what the card shows; it records nothing about the patient.
    PN_CARD_TRACK_ID,
    PN_CARD_VERSION_ID,
    PN_INSURANCE_PREFILLED_ID,
    PN_INSURANCE_ELIGIBILITY_ID,
    `${PN_INSURANCE_ELIGIBILITY_ID}Eligible`,
    ...answerStrings(values[PN_INSURANCE_PREFILLED_ID]).flatMap((id) => [id, `${id}Reference`, `${id}Key`]),
  ]);
  return hasAnyRecordedValue(Object.fromEntries(Object.entries(values).filter(([id]) => !own.has(id))));
}

/**
 * The collapsed card as one status-first line (BAR-2925), e.g. `Committed,
 * benefit verification next · Insurance · Cigna · ESG · Charleston`: the
 * outcome (or "In progress" / "Not started"), the track with its administrator
 * or insurer, the procedure, and the location — the insurance travel location
 * on the Insurance track, else the self-pay location. Parts not known are left
 * out. Takes the answers that apply, so a section that stopped applying names
 * no insurer or travel location.
 */
export function buildPnConsultationLine(answers: PnConsultationAnswers): string {
  const { values } = answers;
  const status =
    getPnConsultationOutcome(answers) ??
    (hasPnConsultationAnswer(answers) ? PN_IN_PROGRESS_STATUS : PN_NOT_STARTED_STATUS);

  const track = trackAfterCall(values);
  const insurer = track === Track.INSURANCE ? answerString(values[PN_INSURER_ID]) : undefined;
  const travelMarket =
    track === Track.INSURANCE ? insuranceTravelMarket(answerString(values[PN_INSURANCE_TRAVEL_ID])) : undefined;

  return [
    status,
    track ? PatientTrackLabel[track] : undefined,
    insurer,
    answerString(values.procedure),
    travelMarket ?? answerString(values.loc),
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Answer ids older drafts used for the insurance card; it lives only on the Coverage now. */
const INSURANCE_CARD_ANSWER_IDS: readonly string[] = [PN_INSURANCE_MEMBER_ID, PN_INSURANCE_GROUP_ID];

/**
 * The answers without the member ID and group number a note drafted before
 * they moved to the Coverage may carry, nor their prefill marks. The note is
 * read by roles that cannot read Coverage (Marketing, Research Fellow,
 * Quality Audit), so the card runs this on every PN draft before it is held,
 * shown or saved. The same object when there is nothing to drop.
 */
export function withoutInsuranceCardAnswers(answers: PnConsultationAnswers): PnConsultationAnswers {
  const { values } = answers;
  const prefilled = values[PN_INSURANCE_PREFILLED_ID];
  const marked = answerStrings(prefilled).some((id) => INSURANCE_CARD_ANSWER_IDS.includes(id));
  if (!marked && !INSURANCE_CARD_ANSWER_IDS.some((id) => id in values)) return answers;

  const kept = Object.fromEntries(Object.entries(values).filter(([id]) => !INSURANCE_CARD_ANSWER_IDS.includes(id)));
  if (marked) {
    kept[PN_INSURANCE_PREFILLED_ID] = answerStrings(prefilled).filter((id) => !INSURANCE_CARD_ANSWER_IDS.includes(id));
  }
  return { ...answers, values: kept };
}

/**
 * Human-readable summary stored on the `text-content` payload, so timeline
 * search and any generic Communication renderer show something meaningful.
 * Deliberately terse — the card itself is the full record.
 *
 * Takes the answers that apply (`answersThatApply`), not the raw draft: a
 * section that stopped applying keeps its answers on the draft but not here.
 */
export function buildPnConsultationSummary(answers: PnConsultationAnswers): string {
  const lines: string[] = ['PN consultation'];

  const procedure = answerString(answers.values.procedure);
  if (procedure) lines.push(`Procedure: ${procedure}`);

  const goal = answerString(answers.values.wn_main);
  if (goal) lines.push(`Goal: ${GOAL_SHORT_LABELS[goal] ?? goal}`);

  const source = answerString(answers.values.source);
  if (source) lines.push(`Heard about us: ${source}`);

  const conditions = readWeightRelatedConditions(answers.values);
  if (conditions.length > 0) lines.push(`Weight-related conditions: ${conditions.join(', ')}`);

  const smoking = answerString(answers.values.ql_smoking);
  if (smoking) lines.push(`Smoking: ${smoking}`);

  const pregnancy = answerString(answers.values.ql_preg);
  if (pregnancy) lines.push(`Planning a pregnancy: ${pregnancy}`);

  // The v2.0 card asks whose job, then that job's employment (BAR-2997). A
  // note answering those reads by them, as the verdict does; a shipped-card
  // answer left on it from before the switch is not repeated.
  const through = answerString(answers.values[PN_BENEFITS_THROUGH_ID]);
  const employmentStatus = answerString(answers.values[PN_EMPLOYMENT_STATUS_ID]);
  const employment = answerString(answers.values[PN_EMPLOYMENT_ID]);
  if (employment && !through && !employmentStatus) lines.push(`Employment: ${employment}`);
  if (through) lines.push(`Benefits through: ${through}`);
  if (employmentStatus) lines.push(`Employment: ${employmentStatus}`);

  const employer = answerString(answers.values[PN_EMPLOYER_ID]);
  if (employer) lines.push(`Employer: ${employer}`);

  const familyEmployer = answerString(answers.values[PN_FAMILY_EMPLOYER_ID]);
  if (familyEmployer) lines.push(`Family member’s employer: ${familyEmployer}`);

  const familyMember = answerString(answers.values[PN_FAMILY_MEMBER_NAME_ID]);
  if (familyMember) lines.push(`Family member: ${familyMember}`);

  const benefit = describeEmployerBenefit(answers);
  if (benefit) lines.push(benefit.label);

  const bookOnceApproved = answerString(answers.values[PN_BOOK_ONCE_APPROVED_ID]);
  if (bookOnceApproved) lines.push(`Book once approved: ${bookOnceApproved}`);

  const employerTask = describeVerificationTask(answers.values[PN_EMPLOYER_TASK_ID], 'Employer');
  if (employerTask) lines.push(employerTask);

  const travel = answerString(answers.values[PN_INSURANCE_TRAVEL_ID]);
  if (travel) lines.push(`Insurance location: ${travel}`);

  const insuranceSource = answerString(answers.values[PN_INSURANCE_SOURCE_ID]);
  if (insuranceSource) lines.push(`Insurance: ${insuranceSource}`);

  const insurer = answerString(answers.values[PN_INSURER_ID]);
  if (insurer) lines.push(`Insurer: ${insurer}`);

  const policyholder = answerString(answers.values[PN_POLICYHOLDER_ID]);
  if (policyholder) lines.push(`Whose plan: ${policyholder}`);

  const eligibility = answerString(answers.values[PN_INSURANCE_ELIGIBILITY_ID]);
  if (eligibility) lines.push(`Likely eligibility: ${eligibility}`);

  const insuranceBook = answerString(answers.values[PN_INSURANCE_BOOK_ONCE_APPROVED_ID]);
  if (insuranceBook) lines.push(`Book once insurance approves: ${insuranceBook}`);

  const insuranceTask = describeVerificationTask(answers.values[PN_INSURANCE_TASK_ID], 'Insurance');
  if (insuranceTask) lines.push(insuranceTask);

  const location = answerString(answers.values.loc);
  if (location) lines.push(`Location: ${location}`);

  const employerLocation = answerString(answers.values[PN_EMPLOYER_LOCATION_ID]);
  if (employerLocation) lines.push(`Employer benefit location: ${employerLocation}`);

  if (answers.procedureDate) {
    const withName = answers.procedureDate.physicianName ? ` with ${answers.procedureDate.physicianName}` : '';
    lines.push(`Procedure date: ${answers.procedureDate.start}${withName}`);
  }

  // Coarse timing, including "not ready to commit" — worth recording as
  // explicitly as a date the patient named.
  const timing = answerString(answers.values.procdate);
  if (timing) lines.push(`Procedure timing: ${timing}`);

  // The v2.0 card's payment types, several at once (BAR-3000), over a shipped-card answer left on the note.
  const paymentTypes = readPaymentTypes(answers.values);
  const payment = answerString(answers.values.pay);
  if (paymentTypes.length > 0) lines.push(`Payment: ${paymentTypes.join(', ')}`);
  else if (payment) lines.push(`Payment: ${payment}`);

  const outcome = getPnConsultationOutcome(answers);
  if (outcome) lines.push(`Outcome: ${outcome}`);

  const motivations = answerStrings(answers.values.motivation);
  if (motivations.length > 0) lines.push(`Motivation: ${motivations.join(', ')}`);

  const barriers = answerStrings(answers.values.barrier);
  if (barriers.length > 0) lines.push(`Barriers: ${barriers.join(', ')}`);

  const funFacts = answerString(answers.values.fun_facts);
  if (funFacts) lines.push(`Notes: ${funFacts}`);

  return lines.join('\n');
}
